<script>
   /**************************************************************/
   /* PCVCalculator                                              */
   /* -------------------------                                  */
   /* component for computing a PV-set                           */
   /**************************************************************/

   import { pcvpca } from '../../../shared/wasm/dist/pca.js';
   import { app_pca_predict } from '../../../shared/utils.js';

   import Select from '../../../shared/controls/Select.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';
   import Spinner from '../../../shared/controls/Spinner.svelte';
   import ButtonCancel from '../../../shared/controls/ButtonCancel.svelte';


   // main properties of the whole app
   export let WASM;
   export let calset;
   export let model;
   export let pvset;
   export let calres;
   export let pvres;
   export let outliers;

   // error handling parameetrs
   let status = 'neutral';
   let errMsg = null;

   // PV-set calculation parameters
   let cvtypeOptions = ['ven', 'rand', 'loo'];
   let cvType = 'ven';

   // promise container for calculator
   let promise;

   // compute number of segments options based on dataset size
   let nsegOptions = calset && calset.values.nrows > 0 ?
      [3, 4, 5, 10, 20].filter(v =>
         v >= (calset.values.nrows / (calset.values.nrows - model.ncomp - 1)) && v <= calset.values.nrows / 2
      ) : [];

   // select first available option for number of segments as initial
   let nseg = nsegOptions.length > 0 ? nsegOptions[0] : 0;

   // if no options available (dataset is too small) - limit CV choice by LOO
   $: if (nsegOptions.length === 0) {
      cvtypeOptions = ['loo'];
      cvType = 'loo';
   }

   // function that computes PV-set using given parameters
   async function computePVSet(calset, model, cv) {
      promise = WASM()
         .then(Module => {
            const cv_type = cv.type == 'loo' ? 0 : cv.type == 'rand' ? 2 : 1;
            const pvvalues = pcvpca(Module, model, calset.values, model.ncomp, cv.nseg, cv_type, 0, outliers);
            pvset = {
               values: pvvalues,
               header: calset.header,
               objindices: calres.objindices,
               objlabels: calres.objlabels,
               varlabels: model.varlabels,
               varindices: model.varindices,
               varvalues: model.varvalues,
               varvaluesName: model.varvaluesName,
               varvaluesUnits: model.varvaluesUnits,
               classes: calres.classes,
               sep: calset.sep,
               dec: calset.dec
            }

            // pv-set is created for outliers free data, hence outliers is empty here
            pvres = app_pca_predict(Module, model, pvset, []);
         })
         .catch((e) => {
            pvset = null;
            pvres = null;
            status = 'error';
            errMsg = e;
         });
   }

   // event handler which triggers the model computation
   function compute(e) {
      status = 'neutral';
      errMsg = null;
      computePVSet(calset, model, {type: cvType, nseg: nseg});
   }

   // function which resets the state of the component
   function reset() {
      status = 'neutral';
      errMsg = null;
      promise = null;
      pvset = null;
   }

   // if data file is empty reset status and error messages
   $: if (model === null) reset();
</script>


{#if (model && !pvset) && status !== 'busy'}
   <Container label='Split type:'>
      <Select id='cvType' bind:value={cvType} options={cvtypeOptions} />
   </Container>

   {#if cvType !== 'loo'}
   <Container label='Segments:'>
      <Select id='nseg' bind:value={nseg} options={nsegOptions} />
   </Container>
   {/if}

   <Container label=' '>
      <Button on:click={compute}>Compute PV-set</Button>
   </Container>
{/if}

{#if promise}

   {#await promise}
      <Container>
         <Spinner />
         <p>Computing PV-set...</p>
      </Container>
   {:then value}
      {#if pvset !== null}
      <Container>
         <p>PV-set is computed: {pvset.values.nrows} &times; {pvset.values.ncols}, {cvType}
            {cvType !== 'loo' ? nseg + ' seg' : ''}</p>
         <ButtonCancel on:click={reset} />
      </Container>
      {/if}
   {:catch error}
      <Container status='error'>
         <p>{error}</p>
      </Container>
   {/await}

{/if}

{#if status === 'error' && errMsg}
   <Container status='error'>
      <p>{errMsg}</p>
   </Container>
{/if}

